import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanDeactivate } from '@angular/router';
import { DialogConfirm } from 'app/dialogs/confirm/dialog-confirm';
import { DialogSave } from 'app/dialogs/save/dialog-save';
import { PendingChangesDialogType } from 'common/constants/dialog-type.constants';
import { Observable, catchError, map, of, switchMap } from 'rxjs';

export interface ComponentCanDeactivate {
    canDeactivate: () => boolean | Observable<boolean>;
    beforeLeaveMessage: string;
    beforeLeaveTitle?: string;

    pendingChangesDialogType?: PendingChangesDialogType;
    saveChangesBeforeLeave?: () => Observable<boolean>;
}

// INFO !!! https://stackoverflow.com/questions/35922071/warn-user-of-unsaved-changes-before-leaving-page
// 1. setja í app-routing.module.ts canDeactivate
// 2. Láta component implements ComponentCanDeactivate.
// 3. setja efst í component formúlu

@Injectable()
export class PendingChangesGuard implements CanDeactivate<ComponentCanDeactivate> {
    constructor(private dialog: MatDialog) {}

    canDeactivate(component: ComponentCanDeactivate): boolean | Observable<boolean> {
        if (!component.canDeactivate || component.canDeactivate()) {
            return true;
        }

        const dialogType = component.pendingChangesDialogType ?? 'confirm';

        if (dialogType === 'save') {
            return this.openSaveDialog(component);
        }

        return this.openConfirmDialog(component);
    }

    private openConfirmDialog(component: ComponentCanDeactivate): Observable<boolean> {
        const dialogRef = this.dialog.open(DialogConfirm, {
            data: {
                title: component.beforeLeaveTitle ?? 'Óvistaðar breytingar',
                message: component.beforeLeaveMessage,
            },
        });

        return dialogRef.afterClosed().pipe(map((confirmed) => confirmed === true));
    }

    private openSaveDialog(component: ComponentCanDeactivate): Observable<boolean> {
        const dialogRef = this.dialog.open(DialogSave, {
            disableClose: true,
            data: {
                title: component.beforeLeaveTitle ?? 'Óvistaðar breytingar',
                message: component.beforeLeaveMessage,
            },
        });

        return dialogRef.afterClosed().pipe(
            switchMap((result: 'save' | 'discard' | 'cancel') => {
                if (result === 'discard') {
                    return of(true);
                }

                if (result === 'cancel' || !result) {
                    return of(false);
                }

                if (!component.saveChangesBeforeLeave) {
                    return of(false);
                }

                return component.saveChangesBeforeLeave().pipe(catchError(() => of(false)));
            })
        );
    }
}
